import type { EbookDocument, EbookQuestion, ProblemBoardAnnotation } from '@wx-weiqi/contracts';
import type { EbookClient } from './ebooks';

export interface ClassroomTextbook {
  readonly id: string;
  readonly title: string;
  readonly questions: readonly EbookQuestion[];
}

function validateQuestionDiagram(question: EbookQuestion) {
  const name = question.title || question.number || question.id;
  if (question.stones.length !== question.size * question.size) {
    throw new Error(`“${name}”棋盘数据不完整，请先在教材中校验并保存该题。`);
  }
  if (
    question.marks.some(
      (mark) =>
        !Number.isInteger(mark.point.x) ||
        !Number.isInteger(mark.point.y) ||
        mark.point.x < 0 ||
        mark.point.y < 0 ||
        mark.point.x >= question.size ||
        mark.point.y >= question.size ||
        (mark.kind === 'LABEL' && !mark.label.trim()),
    )
  ) {
    throw new Error(`“${name}”棋盘标记无效，请先在教材中校验并保存该题。`);
  }
}

/** Read only the saved compilation, in its exact order; do not include recognition drafts. */
export function classroomTextbookFromDocument(document: EbookDocument): ClassroomTextbook {
  const ids = document.data.book.questionIds;
  if (new Set(ids).size !== ids.length) {
    throw new Error('教材编排中有重复题目，请先在“我的教材”中检查并保存。');
  }
  const selected = new Set(ids);
  const byId = new Map<string, EbookQuestion>();
  for (const question of document.data.questions) {
    if (!selected.has(question.id)) continue;
    if (byId.has(question.id)) {
      throw new Error('教材题目编号重复，无法确定原题，请先在“我的教材”中检查并保存。');
    }
    byId.set(question.id, question);
  }
  const questions = ids.map((id) => {
    const question = byId.get(id);
    if (!question) {
      throw new Error('教材编排中有题目缺失，请先在“我的教材”中检查并保存。');
    }
    validateQuestionDiagram(question);
    return question;
  });
  return {
    id: document.id,
    title: document.data.book.title || document.name,
    questions,
  };
}

export async function loadClassroomTextbook(
  client: Pick<EbookClient, 'document'>,
  id: string,
): Promise<ClassroomTextbook> {
  // The existing endpoint authorizes teacher role, document owner and organization.
  // This read must never publish a book, save a draft or start a student attempt.
  const document = await client.document(id);
  if (document.id !== id) throw new Error('教材信息不匹配，请重新选择教材。');
  return classroomTextbookFromDocument(document);
}

/** Root diagram marks only. Answer variations are not played or guessed on the client. */
export function classroomTextbookQuestionAnnotations(
  question: EbookQuestion,
): readonly ProblemBoardAnnotation[] {
  validateQuestionDiagram(question);
  return question.marks.map((mark) =>
    mark.kind === 'LABEL'
      ? { kind: 'LABEL', point: { ...mark.point }, text: mark.label }
      : { kind: mark.kind, point: { ...mark.point } },
  );
}
